import { useCallback, useEffect, useRef, useState } from "react";

const CHECK_INTERVAL_MS = 60 * 1000;
const normalizeVersion = (value) => {
  if (!value) return null;
  try { return new URL(value, window.location.origin).pathname; }
  catch (_) { return value; }
};
const getVersion = (manifest) => normalizeVersion(manifest?.files?.["main.js"] || manifest?.entrypoints?.find((entry) => /main\..+\.js$/.test(entry)));
const getLoadedVersion = () => {
  const script = Array.from(document.scripts).find((item) => /\/static\/js\/main\..+\.js(?:\?|$)/.test(item.src));
  return normalizeVersion(script?.src);
};

export default function useAppUpdate() {
  const baselineRef = useRef(getLoadedVersion());
  const snoozedUntilRef = useRef(0);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const checkForUpdate = useCallback(async () => {
    if (process.env.NODE_ENV !== "production") return;
    try {
      const response = await fetch(`/asset-manifest.json?v=${Date.now()}`, { cache: "no-store", headers: { "Cache-Control": "no-cache" } });
      if (!response.ok) return;
      const version = getVersion(await response.json());
      if (!version) return;
      if (baselineRef.current === null) baselineRef.current = version;
      else if (baselineRef.current !== version && Date.now() >= snoozedUntilRef.current) setUpdateAvailable(true);
    } catch (error) {
      console.warn("[Version] vérification impossible", error);
    }
  }, []);

  useEffect(() => {
    checkForUpdate();
    const interval = window.setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    const onVisibility = () => document.visibilityState === "visible" && checkForUpdate();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", checkForUpdate);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", checkForUpdate);
    };
  }, [checkForUpdate]);

  const refresh = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("_livresto_update", Date.now().toString());
    window.location.replace(url.toString());
  };
  const dismiss = () => {
    snoozedUntilRef.current = Date.now() + 15 * 60 * 1000;
    setUpdateAvailable(false);
  };
  return { updateAvailable, refresh, dismiss };
}
